import { useState } from 'react';
import { useTransactionStore } from '../state/useTransactionStore';
import { parseCSV, getCSVHeaders } from '../utils/csvParser';
import { ColumnMapping, Transaction } from '../types';

export default function Upload() {
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({
    date: '',
    description: '',
    amount: '',
    type: '',
    category: '',
    balance: '',
    checkNumber: '',
  });
  const [previewTransactions, setPreviewTransactions] = useState<Transaction[]>([]);
  const [mappingName, setMappingName] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { addTransactions } = useTransactionStore();
  const saveMapping = useTransactionStore(state => state.saveMapping);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Check file size (limit to 50MB)
    if (selectedFile.size > 50 * 1024 * 1024) {
      setError('File size exceeds 50MB limit');
      return;
    }

    // Check file type
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setProgress(null);
    setPreviewTransactions([]);

    try {
      const headers = await getCSVHeaders(selectedFile);
      setHeaders(headers);

      // Try to auto-detect mappings
      const autoMapping: ColumnMapping = {
        date: headers.find(h => h.toLowerCase().includes('date')) || '',
        description: headers.find(h => h.toLowerCase().includes('desc')) || '',
        amount: headers.find(h => h.toLowerCase().includes('amount')) || '',
        category: headers.find(h => h.toLowerCase().includes('category')) || '',
        balance: headers.find(h => h.toLowerCase().includes('balance')) || '',
        checkNumber: headers.find(h => h.toLowerCase().includes('check')) || '',
      };
      setMapping(autoMapping);
    } catch (err) {
      let errorMessage = 'Error reading file headers';
      if (err instanceof Error) {
        if (err.message.includes('Too many fields')) {
          errorMessage = 'CSV file has inconsistent number of columns. Please check your file format.';
        } else if (err.message.includes('No headers found')) {
          errorMessage = 'No headers found in CSV file. Please ensure your file has a header row.';
        } else if (err.message.includes('empty')) {
          errorMessage = 'CSV file is empty or has no valid data.';
        } else {
          errorMessage = err.message;
        }
      }
      setError(errorMessage);
      console.error('CSV header error:', err);
    }
  };

  const handleMappingChange = (key: keyof ColumnMapping, value: string) => {
    setMapping((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const isValidMapping = () => {
    // Check required fields
    const hasRequiredFields = mapping.date && mapping.description;
    
    // Check for amount column
    const hasAmount = mapping.amount;
    
    return hasRequiredFields && hasAmount;
  };

  const handlePreview = async () => {
    if (!file) return;

    try {
      setError(null);
      setProgress(0);
      const transactions = await parseCSV(file, mapping, (progress) => {
        setProgress(progress);
      });
      setPreviewTransactions(transactions);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to parse CSV file');
    }
  };

  const handleSave = async () => {
    if (!file || previewTransactions.length === 0) return;

    try {
      // Save transactions
      addTransactions(previewTransactions);

      // Save mapping if name provided
      if (mappingName) {
        saveMapping(mappingName, mapping);
      }

      // Reset form
      setFile(null);
      setHeaders([]);
      setMapping({
        date: '',
        description: '',
        amount: '',
        type: '',
        category: '',
        balance: '',
        checkNumber: '',
      });
      setPreviewTransactions([]);
      setMappingName('');
      setProgress(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error saving transactions';
      setError(errorMessage);
      console.error('Save error:', err);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;

    // Check file size (limit to 50MB)
    if (droppedFile.size > 50 * 1024 * 1024) {
      setError('File size exceeds 50MB limit');
      return;
    }

    // Check file type
    if (!droppedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setFile(droppedFile);
    setError(null);
    setProgress(null);
    setPreviewTransactions([]);

    try {
      const headers = await getCSVHeaders(droppedFile);
      setHeaders(headers);

      // Try to auto-detect mappings
      const autoMapping: ColumnMapping = {
        date: headers.find(h => h.toLowerCase().includes('date')) || '',
        description: headers.find(h => h.toLowerCase().includes('desc')) || '',
        amount: headers.find(h => h.toLowerCase().includes('amount')) || '',
        category: headers.find(h => h.toLowerCase().includes('category')) || '',
        balance: headers.find(h => h.toLowerCase().includes('balance')) || '',
        checkNumber: headers.find(h => h.toLowerCase().includes('check')) || '',
      };
      setMapping(autoMapping);
    } catch (err) {
      let errorMessage = 'Error reading file headers';
      if (err instanceof Error) {
        if (err.message.includes('Too many fields')) {
          errorMessage = 'CSV file has inconsistent number of columns. Please check your file format.';
        } else if (err.message.includes('No headers found')) {
          errorMessage = 'No headers found in CSV file. Please ensure your file has a header row.';
        } else if (err.message.includes('empty')) {
          errorMessage = 'CSV file is empty or has no valid data.';
        } else {
          errorMessage = err.message;
        }
      }
      setError(errorMessage);
      console.error('CSV header error:', err);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Upload Transactions</h2>
      
      <div className="space-y-6">
        {/* File Upload Section */}
        <div 
          className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="mt-4 flex text-sm text-gray-600 dark:text-gray-400">
              <label
                htmlFor="file-upload"
                className="relative cursor-pointer bg-white dark:bg-gray-700 rounded-md font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
              >
                <span>Upload a file</span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  accept=".csv"
                  className="sr-only"
                  onChange={handleFileChange}
                />
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">CSV files only</p>
          </div>
        </div>

        {/* Column Mapping Section */}
        {file && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Map Columns</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {Object.entries(mapping).map(([key, value]) => {
                const fieldKey = key as keyof ColumnMapping;
                return (
                  <div key={key} className="space-y-2">
                    <label
                      htmlFor={key}
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                      {key === 'date' || key === 'description' || key === 'amount' ? (
                        <span className="text-red-500 ml-1">*</span>
                      ) : null}
                    </label>
                    <select
                      id={key}
                      value={value}
                      onChange={(e) => handleMappingChange(fieldKey, e.target.value)}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="">Select a column</option>
                      {headers.map((header) => (
                        <option key={header} value={header}>
                          {header}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {progress && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
              <span>Progress: {progress}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
              <div
                className={`bg-indigo-600 h-2.5 rounded-full ${
                  progress < 100 ? '' : 'bg-green-600'
                }`}
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400 dark:text-red-300"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upload Button */}
        {file && (
          <button
            onClick={handlePreview}
            disabled={!isValidMapping()}
            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
              isValidMapping()
                ? 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            Preview Transactions
          </button>
        )}

        {/* Preview Table */}
        {previewTransactions.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Preview</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Category
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {previewTransactions.slice(0, 5).map((transaction, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(transaction.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {transaction.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        ${transaction.amount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {transaction.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {transaction.category}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewTransactions.length > 5 && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Showing 5 of {previewTransactions.length} transactions
                </p>
              )}
            </div>
          </div>
        )}

        {/* Save Button */}
        {previewTransactions.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center space-x-4">
              <input
                type="text"
                value={mappingName}
                onChange={(e) => setMappingName(e.target.value)}
                placeholder="Save mapping as (optional)"
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Save Transactions
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 